import React from 'react';
import { api } from '../lib/api';
import { Modal, InstructorCard, Badge, Btn } from './index';
import { palette as T, type } from '../design';

// An instructor is listed in the marketplace only in this status (mirrors INSTRUCTOR_STATUS.ACTIVE).
const LISTED_STATUS = 'active';

const sans = "'Plus Jakarta Sans', sans-serif";
const sectionTitle = { fontFamily: sans, fontWeight: 700, fontSize: type.label, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '20px 0 6px' };
const bodyText = { fontFamily: sans, fontSize: type.body, color: T.walnut, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' };

// Admin's look at an instructor's public profile before verifying them (#306). It reads the admin
// preview route, which returns the same shape the marketplace shows, for an instructor in any
// status, so admin can judge someone who is not listed yet.
//
// `onVerify(id)` is the dashboard's existing verify action. When it resolves, the preview reloads
// so admin sees the Verified badge the marketplace card will now show.
export function InstructorPreviewModal({ instructorId, onClose, onVerify, verifying = false }) {
  const [instructor, setInstructor] = React.useState(null);
  const [error, setError] = React.useState(null);
  const titleId = React.useId();

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const res = await api.get(`/admin/instructors/${instructorId}/preview`);
      setInstructor(res.instructor);
    } catch (err) {
      // Never fail silently: say what happened and what to do next.
      console.error('Instructor preview failed:', err);
      setError(err.status === 404
        ? 'We could not find this instructor. Close this window and refresh the list.'
        : 'We could not load this profile. Close this window and try again.');
    }
  }, [instructorId]);

  React.useEffect(() => { load(); }, [load]);

  const handleVerify = async () => {
    await onVerify(instructor.id);
    await load();
  };

  const footer = (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
      {instructor && !instructor.verified && onVerify && (
        <Btn variant="terracotta" onClick={handleVerify} disabled={verifying}>{verifying ? 'Verifying…' : 'Verify'}</Btn>
      )}
      <Btn variant="ghost" onClick={onClose}>Close</Btn>
    </div>
  );

  return (
    <Modal onClose={onClose} maxWidth={480} footer={footer} labelledBy={titleId}>
      <div style={{ padding: '28px 28px 24px' }}>
        <h3 id={titleId} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, color: T.espresso, margin: '0 40px 4px 0' }}>Profile preview</h3>
        <p style={{ fontFamily: sans, fontSize: type.body, color: T.driftwood, margin: '0 0 16px' }}>This is what students see in the marketplace.</p>

        {error && <p role="alert" style={{ ...bodyText, color: T.errorRed }}>{error}</p>}
        {!error && !instructor && <p style={bodyText}>Loading the profile…</p>}

        {instructor && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              <Badge variant={instructor.status === LISTED_STATUS ? 'sage' : 'gold'}>
                {instructor.status === LISTED_STATUS ? 'Listed in the marketplace' : 'Not listed yet'}
              </Badge>
              <Badge variant={instructor.verified ? 'sage' : 'gold'}>
                {instructor.verified ? 'Verified' : 'Not verified yet'}
              </Badge>
            </div>
            <div style={{ maxWidth: 320 }}>
              <InstructorCard instructor={instructor} />
            </div>
            <p style={sectionTitle}>About</p>
            <p style={bodyText}>{instructor.bio || 'No bio yet.'}</p>
            <p style={sectionTitle}>Teaching style</p>
            <p style={bodyText}>{instructor.teachingStyle || 'No teaching style yet.'}</p>
          </>
        )}
      </div>
    </Modal>
  );
}
